import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { type OnClick } from '../constants';

import dibsCss from 'dibs-css';
import styles from '../styles/styles.scss';

import { type ProductTitle_item$key } from './__generated__/ProductTitle_item.graphql';

const itemFragment = graphql`
    fragment ProductTitle_item on Item {
        serviceId
        isArt
        title
        classification {
            creationDate
        }
        creators {
            creator {
                displayName
            }
        }
        linkData {
            ...SeoLink_linkData
        }
    }
`;

const CREATOR_UNKNOWN = 'unknown';

type Props = {
    item: ProductTitle_item$key;
    isSponsored?: boolean;
    onClick?: OnClick;
};

export const ProductTitle: FC<Props> = ({ item: itemRef, isSponsored, onClick }) => {
    const item = useFragment(itemFragment, itemRef);
    const { isArt, title, classification, creators, linkData } = item;
    const itemId = item?.serviceId || '';
    const creationDate = classification?.creationDate;
    const attributionCreatorName = creators?.[0]?.creator?.displayName;
    const showAttributionCreatorName =
        attributionCreatorName && attributionCreatorName.toLowerCase() !== CREATOR_UNKNOWN;
    const dataTn = `item-tile-title-anchor${isSponsored ? '-sponsored' : ''}`;

    return (
        <HeadingLevel>
            {Heading => (
                <SeoLink
                    dataTn={dataTn}
                    // data-pk attribute is used by dibs-toho (MG)
                    dataPk={itemId}
                    onClick={onClick}
                    className={classnames(styles.tileLink, dibsCss.lineClamp2, dibsCss.wFull)}
                    linkData={linkData}
                >
                    <Heading
                        className={classnames(
                            styles.tileLinkHeading,
                            dibsCss.textBodySmallNormal,
                            dibsCss.textNoir,
                            dibsCss.wFull,
                            dibsCss.m0
                        )}
                    >
                        {isArt ? (
                            <>
                                {showAttributionCreatorName && (
                                    <span className={dibsCss.flex}>
                                        <span
                                            className={dibsCss.truncate}
                                            data-tn="portfolio-row-creator-name"
                                        >
                                            {attributionCreatorName}
                                        </span>
                                    </span>
                                )}
                                <span className={classnames(dibsCss.flex, dibsCss.wFull)}>
                                    <span
                                        className={dibsCss.truncate}
                                        data-tn="portfolio-row-product-name"
                                    >
                                        {title}
                                    </span>
                                    {creationDate && (
                                        <span
                                            className={dibsCss.whitespaceNowrap}
                                        >{`, ${creationDate}`}</span>
                                    )}
                                </span>
                            </>
                        ) : (
                            title
                        )}
                    </Heading>
                </SeoLink>
            )}
        </HeadingLevel>
    );
};
